/*
 * CalmEden cart drawer.
 *
 * 1:1 behavioural port of the owner-operated source storefront's cart
 * stack: assets/cart.js, assets/cart-drawer.js, assets/quantity-popover.js and
 * the cart-related helpers out of assets/global.js. Source files were captured
 * on 2026-08-04; sha256 manifest lives in
 * /home/aibot/codex-runs/calmeden-sleep-drops-pdp-20260804/cart-assets/manifest.json
 *
 * Deliberate deviations from the source (all documented in cart-build-report.md):
 *   1. The source binds the drawer opener to `#cart-icon-bubble`. CalmEden's
 *      header markup is owned by another lane, so we delegate off every cart
 *      link instead and update the existing `.ce-header__count` badge ourselves.
 *   2. The source re-renders a separate `cart-icon-bubble` section. We only
 *      request the `cart-drawer` section and patch the count from the cart JSON.
 *   3. Section lookups are filtered to elements that actually exist so a missing
 *      optional target can never throw.
 */
(function () {
  "use strict";

  var ON_CHANGE_DEBOUNCE_TIMER = 300;

  var routes = (window.routes = window.routes || {});
  routes.cart_url = routes.cart_url || "/cart";
  routes.cart_add_url = routes.cart_add_url || "/cart/add";
  routes.cart_change_url = routes.cart_change_url || "/cart/change";
  routes.cart_update_url = routes.cart_update_url || "/cart/update";

  window.cartStrings = window.cartStrings || {
    error: "There was an error while updating your cart. Please try again.",
    quantityError: "You can only add [quantity] of this item to your cart.",
  };

  /* ---------------------------------------------------------------- helpers */

  function getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
      ),
    );
  }

  var trapFocusHandlers = {};

  function trapFocus(container, elementToFocus) {
    if (!container) return;
    elementToFocus = elementToFocus || container;
    var elements = getFocusableElements(container);
    var first = elements[0];
    var last = elements[elements.length - 1];

    removeTrapFocus();

    trapFocusHandlers.focusin = function (event) {
      if (
        event.target !== container &&
        event.target !== last &&
        event.target !== first
      )
        return;
      document.addEventListener("keydown", trapFocusHandlers.keydown);
    };
    trapFocusHandlers.focusout = function () {
      document.removeEventListener("keydown", trapFocusHandlers.keydown);
    };
    trapFocusHandlers.keydown = function (event) {
      if (event.code.toUpperCase() !== "TAB") return;
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }
      if (
        (event.target === container || event.target === first) &&
        event.shiftKey
      ) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener("focusout", trapFocusHandlers.focusout);
    document.addEventListener("focusin", trapFocusHandlers.focusin);

    if (elementToFocus) elementToFocus.focus();
  }

  function removeTrapFocus(elementToFocus) {
    document.removeEventListener("focusin", trapFocusHandlers.focusin);
    document.removeEventListener("focusout", trapFocusHandlers.focusout);
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
    if (elementToFocus) elementToFocus.focus();
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function fetchConfig(type) {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/" + (type || "json"),
      },
    };
  }

  /*
   * The `is-empty` class lives on the <cart-drawer> host, but the Section
   * Rendering API only replaces the markup *inside* it, so the class survives a
   * re-render. Left stale it applies `cart-drawer.is-empty .drawer__header
   * {display:none}` and `cart-drawer.is-empty .drawer__inner{display:grid;
   * grid-template-rows:1fr;align-items:center}`, which hides the heading and
   * collapses the whole items region (and its product image) to zero height.
   * The source theme clears it from its product form; we do it centrally.
   */
  function syncEmptyState(isEmpty) {
    document
      .querySelectorAll(
        "cart-drawer, cart-drawer-items, cart-items, #main-cart-footer",
      )
      .forEach(function (el) {
        el.classList.toggle("is-empty", isEmpty);
      });
  }

  function updateHeaderCartCount(itemCount) {
    document.querySelectorAll(".ce-header__count").forEach(function (badge) {
      badge.textContent = itemCount;
      badge.hidden = itemCount === 0;
    });
    document.querySelectorAll('a[href$="/cart"]').forEach(function (link) {
      if (link.hasAttribute("aria-label")) {
        link.setAttribute("aria-label", "Cart, " + itemCount + " items");
      }
    });
  }

  /*
   * Checkout gating is owned by the transaction barrier below (see
   * applyCheckoutGate / setDesiredCheckoutDisabled); nothing else writes the
   * buttons' disabled state.
   */
  function setCheckoutDisabled(disabled) {
    /* legacy alias: only ever called with the PROVEN desired state */
    setDesiredCheckoutDisabled(disabled);
  }

  /* The theme advertises gifts when the global config island is present. */
  function giftSystemExpected() {
    return !!document.querySelector("[data-cb-global]");
  }

  function isValidCartPayload(cart) {
    return !!(
      cart &&
      typeof cart.item_count === "number" &&
      Array.isArray(cart.items)
    );
  }

  /*
   * The one validated cart reader, used by the add pipeline, recovery and
   * refresh: rejects on a non-2xx response or a malformed body, so no caller
   * can ever treat an unknown cart as truth.
   */
  function readCart() {
    return fetch(routes.cart_url + ".js?cb=" + Date.now()).then(
      function (response) {
        if (!response || !response.ok) throw new Error("cart read failed");
        return response.json().then(function (cart) {
          if (!isValidCartPayload(cart)) {
            throw new Error("invalid cart payload");
          }
          return cart;
        });
      },
    );
  }

  /*
   * The one validated drawer-section reader, used by recovery and refresh:
   * rejects on a non-2xx response, malformed JSON, or a section that is not a
   * structurally real cart drawer (see isValidDrawerSection below).
   */
  function readDrawerSection() {
    return fetch(
      routes.cart_url + "?sections=cart-drawer&cb=" + Date.now(),
    ).then(function (response) {
      if (!response || !response.ok)
        throw new Error("drawer section read failed");
      return response.json().then(function (sections) {
        if (!isValidDrawerSection(sections))
          throw new Error("invalid cart-drawer section");
        return sections["cart-drawer"];
      });
    });
  }

  /* --------------------------------------------------- transaction barrier */

  /*
   * ONE shared serial transaction barrier for every cart mutation path:
   * generic add, bundle add, quantity/removal, shipping-insurance add,
   * recovery, refresh, boot/bfcache reconcile and the final checkout gate.
   *
   *   - transact(work) ALWAYS enqueues; nesting is never inferred from a
   *     global flag. Internal composition inside an owning transaction calls
   *     the private *Now helpers (refreshDrawerNow, restoreTrueCartStateNow)
   *     directly.
   *   - The gate is computed state, never a captured node:
   *         effectiveDisabled = desiredCheckoutDisabled || transactionBusy()
   *     and applyCheckoutGate() re-applies it to whatever checkout buttons
   *     exist right now, so a button created by a DOM replacement inherits
   *     the gate immediately.
   *   - A flow can never release checkout while work is queued or active: it
   *     only records the last PROVEN desired state, which the barrier applies
   *     when the queue drains. desiredCheckoutDisabled starts null (no proven
   *     state): while null, a drained barrier leaves the server-rendered
   *     button state untouched instead of inventing one.
   */
  var txDepth = 0;
  var txChain = Promise.resolve();
  var desiredCheckoutDisabled = null;
  var lastAuthoritativeCart = null;

  function transactionBusy() {
    return txDepth > 0;
  }

  function applyCheckoutGate() {
    var busy = txDepth > 0;
    ["CartDrawer-Checkout", "checkout"].forEach(function (id) {
      var button = document.getElementById(id);
      if (!button) return;
      if (busy) {
        button.disabled = true;
      } else if (desiredCheckoutDisabled !== null) {
        button.disabled = desiredCheckoutDisabled;
      }
    });
  }

  function setDesiredCheckoutDisabled(disabled) {
    desiredCheckoutDisabled = disabled;
    applyCheckoutGate();
  }

  function applyAuthoritativeCart(cart) {
    if (!isValidCartPayload(cart)) throw new Error("invalid cart payload");
    lastAuthoritativeCart = cart;
    syncEmptyState(cart.item_count === 0);
    updateHeaderCartCount(cart.item_count);
    setDesiredCheckoutDisabled(cart.item_count === 0);
    return cart;
  }

  function getLastAuthoritativeCart() {
    return lastAuthoritativeCart;
  }

  /* Resolve only once the entire serial barrier is drained. A checkout
     handoff cannot use a disabled successful-control while txDepth > 0, and
     must also wait for work that was queued after its own reconciliation. */
  function waitForTransactionsIdle() {
    var observed = txChain;
    return observed.then(function () {
      if (txDepth === 0 && txChain === observed) return;
      return waitForTransactionsIdle();
    });
  }

  function transact(work) {
    txDepth++;
    applyCheckoutGate();
    var run = txChain.then(function () {
      return Promise.resolve().then(work);
    });
    var done = run.then(
      function (value) {
        txDepth--;
        applyCheckoutGate();
        return value;
      },
      function (err) {
        txDepth--;
        applyCheckoutGate();
        throw err;
      },
    );
    txChain = done.catch(function () {});
    return done;
  }

  /* After any DOM replacement that may have created NEW checkout nodes:
     re-apply the effective gate synchronously, before any await can let a
     paint show a fresh server-rendered ENABLED button while a mutation is
     still pending. */
  function regateIfBusy() {
    applyCheckoutGate();
  }

  /* A validated direct add for the no-drawer fallbacks: non-2xx and
     malformed bodies reject, and the response must echo the requested
     variant ids. */
  function jsonValidatedAdd(items) {
    return fetch(routes.cart_add_url + ".js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ items: items }),
    }).then(function (response) {
      return response.json().then(function (parsed) {
        if (!response.ok) {
          throw new Error(
            (parsed && (parsed.description || parsed.message)) ||
              "add failed: HTTP " + response.status,
          );
        }
        if (parsed.status)
          throw new Error(parsed.description || parsed.message);
        verifyAddResponse({ items: items }, parsed);
        return parsed;
      });
    });
  }

  /* -------------------------------------------------------- checkout warm-up */

  /*
   * Shave the connection setup off the checkout navigation.
   *
   * Deliberately preconnect only — NOT `<link rel="prefetch" href="/checkout">`.
   * A GET of /checkout is not a free read: it materialises a checkout server
   * side, which would create phantom "reached checkout" sessions and abandoned
   * checkouts (and therefore abandoned-cart emails) for every shopper who only
   * ever opened the drawer. Preconnect gets the DNS + TCP + TLS handshake out
   * of the way, which is the part that is actually on the critical path, with
   * no business side effect.
   *
   * The CHECKOUT button stays a native `type="submit"` for CartDrawer-Form
   * (which is what commits pending quantity edits) and nothing listens for its
   * click. Its `disabled` attribute IS gated, though: from the add-to-cart tap
   * until the add response, the authoritative server-rendered drawer section
   * and any gift reconciliation have all settled, and again around quantity
   * edits and removals. The optimistic placeholder must never be checkable.
   */
  var checkoutWarmed = false;

  function warmCheckout() {
    if (checkoutWarmed) return;
    checkoutWarmed = true;
    ["https://cdn.shopify.com", "https://checkout.shopifycs.com"].forEach(
      function (href) {
        var link = document.createElement("link");
        link.rel = "preconnect";
        link.href = href;
        link.crossOrigin = "";
        document.head.appendChild(link);
      },
    );
  }

  /* ------------------------------------------------- optimistic add-to-cart */

  /*
   * The drawer used to stay closed until /cart/add.js came back with a fully
   * rendered `cart-drawer` section, so a tap on ADD TO CART did nothing visible
   * for a whole round trip. Now the drawer opens on the same frame as the
   * submit and paints a placeholder line built from what the page already
   * knows, and the real section render replaces it when it lands.
   *
   * Everything here is best-effort: if a field cannot be resolved the row is
   * simply rendered without it. The placeholder is never authoritative — it is
   * wiped by renderContents() on success and by the true cart state on failure,
   * so it can never leave a wrong cart on screen.
   */
  function readOptimisticLine(form, formData) {
    var line = { title: "", image: "", quantity: 1, sellingPlan: "" };

    var quantity = parseInt(formData.get("quantity"), 10);
    if (quantity > 0) line.quantity = quantity;

    // Only trust page-level metadata when the page really is a single product
    // page, otherwise a quick-add on a collection would borrow the wrong title.
    var onProductPage = /\/products\//.test(window.location.pathname);
    var scope = form.closest("[data-product-title]");
    var meta = function (property) {
      var el = document.querySelector('meta[property="' + property + '"]');
      return el ? el.content : "";
    };

    var title =
      form.getAttribute("data-product-title") ||
      (scope ? scope.getAttribute("data-product-title") : "") ||
      (onProductPage ? meta("og:title") : "");
    line.title = String(title || "")
      .replace(/\s+/g, " ")
      .trim();

    var img =
      (
        scope ||
        form.closest('.product, [class*="pdp"]') ||
        document
      ).querySelector(
        '.product__media img, .product-media img, [class*="gallery"] img',
      ) || null;
    if (img && (img.currentSrc || img.src)) {
      line.image = img.currentSrc || img.src;
    } else if (onProductPage) {
      line.image = meta("og:image");
    }

    // Subscription lines carry their cadence as a visible line property
    // ("Deliver every 4 weeks"); keep it in the placeholder so the row does not
    // change shape when the real render lands.
    formData.forEach(function (value, key) {
      if (line.sellingPlan) return;
      if (key.indexOf("properties[") !== 0 || key.indexOf("properties[_") === 0)
        return;
      if (String(value).trim()) line.sellingPlan = String(value).trim();
    });

    return line;
  }

  function escapeHTML(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      }[c];
    });
  }

  function optimisticRowHTML(line) {
    return (
      '<tr class="cart-item cart-item--pending" role="row" aria-busy="true">' +
      '<td class="cart-item__media" role="cell">' +
      (line.image
        ? '<img class="cart-item__image" src="' +
          escapeHTML(line.image) +
          '" alt="" width="150" height="150">'
        : "") +
      "</td>" +
      '<td class="cart-item__details" role="cell">' +
      '<span class="cart-item__name h4 break">' +
      escapeHTML(line.title) +
      "</span>" +
      (line.sellingPlan
        ? '<p class="product-option">' + escapeHTML(line.sellingPlan) + "</p>"
        : "") +
      (line.note
        ? '<p class="product-option">' + escapeHTML(line.note) + "</p>"
        : "") +
      "</td>" +
      '<td class="cart-item__totals right" role="cell">' +
      '<div class="cart-item__price-wrapper"><span class="cart-item__price-placeholder"></span></div>' +
      "</td>" +
      '<td class="cart-item__quantity" role="cell"></td>' +
      "</tr>"
    );
  }

  var OPTIMISTIC_TABLE_HTML =
    '<div class="drawer__cart-items-wrapper"><table class="cart-items" role="table">' +
    '<thead role="rowgroup"><tr role="row">' +
    '<th id="CartDrawer-ColumnProductImage" role="columnheader"><span class="visually-hidden">Product image</span></th>' +
    '<th id="CartDrawer-ColumnProduct" class="caption-with-letter-spacing" scope="col" role="columnheader">Product</th>' +
    '<th id="CartDrawer-ColumnTotal" class="right caption-with-letter-spacing" scope="col" role="columnheader">Total</th>' +
    '<th id="CartDrawer-ColumnQuantity" role="columnheader"><span class="visually-hidden">Quantity</span></th>' +
    '</tr></thead><tbody role="rowgroup"></tbody></table></div>';

  function paintOptimisticLines(drawer, lines) {
    var contents = drawer.querySelector("#CartDrawer-CartItems");
    if (!contents) return;

    // The cart was empty: the section was rendered without a table at all.
    var tbody = contents.querySelector(".cart-items tbody");
    if (!tbody) {
      contents.insertAdjacentHTML("afterbegin", OPTIMISTIC_TABLE_HTML);
      tbody = contents.querySelector(".cart-items tbody");
    }
    if (!tbody) return;

    syncEmptyState(false);
    var inner = drawer.querySelector(".drawer__inner");
    if (inner) inner.classList.remove("is-empty");

    var added = 0;
    lines.forEach(function (line) {
      tbody.insertAdjacentHTML("beforeend", optimisticRowHTML(line));
      added += line.quantity || 1;
    });

    // CHECKOUT is NOT un-disabled here: the optimistic placeholder is not
    // authoritative, so the button stays gated until addItems() settles.

    var badge = document.querySelector(".ce-header__count");
    var current = badge ? parseInt(badge.textContent, 10) : NaN;
    if (!isNaN(current)) updateHeaderCartCount(current + added);

    var errors = document.getElementById("CartDrawer-CartErrors");
    if (errors) errors.textContent = "";
  }

  /*
   * Failure path. Never reload the page and never leave the placeholder up.
   * The gift reconciler runs FIRST and must prove its zero-op postcondition
   * before anything is enabled; then the authoritative drawer section and the
   * cart body are staged together and rendered atomically. Fail closed: every
   * read goes through the validated readers, checkout is only touched when
   * the cart is KNOWN, and any uncertainty rejects - the caller keeps
   * checkout gated and shows recovery text.
   */
  function restoreTrueCartStateNow(drawer, message) {
    var bundle = window.CalmEdenBundle;
    if (
      giftSystemExpected() &&
      (!bundle || typeof bundle.reconcile !== "function")
    ) {
      /* The theme advertises gifts but the reconciler is gone: the cart can
         never be proven clean, so recovery fails closed. */
      return Promise.reject(new Error("gift reconciliation unavailable"));
    }
    return readCart()
      .then(function () {
        /* Reconcile and prove the gift zero-op postcondition BEFORE anything
           is enabled. */
        return bundle && typeof bundle.reconcile === "function"
          ? bundle.reconcile()
          : false;
      })
      .then(function () {
        /* refreshDrawerNow stages and validates EVERY present cart surface
           plus the cart body before any write, renders atomically, re-applies
           the effective gate, and only then records the proven final state.
           Private composition: this runs inside the owning transaction. */
        return refreshDrawerNow();
      })
      .then(function () {
        var errors = document.getElementById("CartDrawer-CartErrors");
        if (errors) errors.textContent = message || window.cartStrings.error;
      });
  }

  /* -------------------------------------------------- quantity-input element */

  if (!customElements.get("quantity-input")) {
    customElements.define(
      "quantity-input",
      class QuantityInput extends HTMLElement {
        constructor() {
          super();
          this.input = this.querySelector("input");
          this.changeEvent = new Event("change", { bubbles: true });
          this.input.addEventListener(
            "change",
            this.validateQtyRules.bind(this),
          );
          this.querySelectorAll("button").forEach(
            function (button) {
              button.addEventListener("click", this.onButtonClick.bind(this));
            }.bind(this),
          );
        }

        connectedCallback() {
          this.validateQtyRules();
        }

        onButtonClick(event) {
          event.preventDefault();
          var previousValue = this.input.value;
          if (event.target.closest("button").name === "plus") {
            this.input.stepUp();
          } else {
            this.input.stepDown();
          }
          if (previousValue !== this.input.value)
            this.input.dispatchEvent(this.changeEvent);
        }

        validateQtyRules() {
          var value = parseInt(this.input.value, 10);
          if (this.input.min) {
            var min = parseInt(this.input.min, 10);
            var minus = this.querySelector(".quantity__button[name='minus']");
            if (minus) minus.classList.toggle("disabled", value <= min);
          }
          if (this.input.max) {
            var max = parseInt(this.input.max, 10);
            var plus = this.querySelector(".quantity__button[name='plus']");
            if (plus) plus.classList.toggle("disabled", value >= max);
          }
        }
      },
    );
  }

  /* ----------------------------------------------------- cart-remove-button */

  if (!customElements.get("cart-remove-button")) {
    customElements.define(
      "cart-remove-button",
      class CartRemoveButton extends HTMLElement {
        constructor() {
          super();
          this.addEventListener("click", (event) => {
            event.preventDefault();
            var owner =
              this.closest("cart-items") || this.closest("cart-drawer-items");
            if (owner) owner.updateQuantity(this.dataset.index, 0);
          });
        }
      },
    );
  }

  /* ------------------------------------------------------------- cart-items */

  class CartItems extends HTMLElement {
    constructor() {
      super();
      this.lineItemStatusElement =
        document.getElementById("shopping-cart-line-item-status") ||
        document.getElementById("CartDrawer-LineItemStatus");
      /* Gate checkout SYNCHRONOUSLY on the raw quantity change, before the
         300ms debounce below even starts the mutation; the gate is then held
         through the mutation, the gift reconcile, the authoritative refresh
         and the final postcondition. */
      this.addEventListener("change", (event) => {
        if (
          event.target.classList &&
          event.target.classList.contains("quantity__input")
        ) {
          /* the cart is unproven from this moment until the mutation settles */
          setDesiredCheckoutDisabled(true);
        }
      });
      this.addEventListener(
        "change",
        debounce((event) => {
          this.onChange(event);
        }, ON_CHANGE_DEBOUNCE_TIMER),
      );
    }

    onChange(event) {
      if (!event.target.classList.contains("quantity__input")) return;
      Promise.resolve(
        this.updateQuantity(
          event.target.dataset.index,
          event.target.value,
          document.activeElement
            ? document.activeElement.getAttribute("name")
            : null,
        ),
      ).catch(function () {
        /* gate + error surface are already handled inside; this only keeps
           the event-handler path free of unhandled-rejection noise */
      });
    }

    getSectionsToRender() {
      var sections = [];
      var items = document.getElementById("main-cart-items");
      if (items) {
        sections.push({
          id: "main-cart-items",
          section: items.dataset.id,
          /* Both full-cart targets are rendered by the same Shopify section.
             The selector therefore has to identify the target inside that
             shared response, not merely the first generic .js-contents. */
          selector: "#main-cart-items .js-contents",
        });
      }
      var footer = document.getElementById("main-cart-footer");
      if (footer) {
        sections.push({
          id: "main-cart-footer",
          section: footer.dataset.id,
          selector: "#main-cart-footer .js-contents",
        });
      }
      return sections;
    }

    updateQuantity(line, quantity, name) {
      this.enableLoading(line);

      var sectionsToRender = this.getSectionsToRender();
      var body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: sectionsToRender
          .map(function (section) {
            return section.section;
          })
          .filter(function (section, index, all) {
            return all.indexOf(section) === index;
          }),
        sections_url: window.location.pathname,
      });

      return transact(() => {
        return fetch(
          routes.cart_change_url + ".js",
          Object.assign(fetchConfig(), { body: body }),
        )
          .then(function (response) {
            return response.text().then(function (text) {
              return { ok: response.ok, status: response.status, text: text };
            });
          })
          .then((result) => {
            var parsedState;
            try {
              parsedState = JSON.parse(result.text);
            } catch (e) {
              throw new Error("invalid change payload");
            }
            var quantityElement =
              document.getElementById("Quantity-" + line) ||
              document.getElementById("Drawer-quantity-" + line);
            var items = document.querySelectorAll(".cart-item");

            if (parsedState && parsedState.errors) {
              /* An errors body is NOT enough to re-enable: the cart state is
                 uncertain, so run validated recovery - which itself
                 reconciles, proves the gift zero-op postcondition and
                 atomically renders the authoritative surfaces. A recovery
                 (or missing-bundle) failure rejects into the outer catch,
                 which keeps the desired state gated and surfaces the error. */
              if (quantityElement)
                quantityElement.value = quantityElement.getAttribute("value");
              this.updateLiveRegions(line, parsedState.errors);
              return restoreTrueCartStateNow(
                document.querySelector("cart-drawer"),
                parsedState.errors,
              );
            }
            if (!result.ok) {
              throw new Error("change failed: HTTP " + result.status);
            }

            /* Stage and validate EVERYTHING before the first live DOM write:
               a missing section or missing selector means zero mutation and a
               still-gated control set. */
            validateChangeState(parsedState, sectionsToRender);

            syncEmptyState(parsedState.item_count === 0);
            var cartDrawerWrapper = document.querySelector("cart-drawer");

            sectionsToRender.forEach(function (section) {
              var container = document.getElementById(section.id);
              if (!container) return;
              var elementToReplace =
                container.querySelector(section.selector) || container;
              elementToReplace.innerHTML = getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector,
              );
            });
            /* DOM replaced: re-gate synchronously, before any await. */
            regateIfBusy();

            updateHeaderCartCount(parsedState.item_count);

            var updatedValue = parsedState.items[line - 1]
              ? parsedState.items[line - 1].quantity
              : undefined;
            var message = "";
            if (
              quantityElement &&
              items.length === parsedState.items.length &&
              updatedValue !== parseInt(quantityElement.value, 10)
            ) {
              message =
                typeof updatedValue === "undefined"
                  ? window.cartStrings.error
                  : window.cartStrings.quantityError.replace(
                      "[quantity]",
                      updatedValue,
                    );
            }
            this.updateLiveRegions(line, message);

            var lineItem =
              document.getElementById("CartItem-" + line) ||
              document.getElementById("CartDrawer-Item-" + line);
            if (
              lineItem &&
              name &&
              lineItem.querySelector('[name="' + name + '"]')
            ) {
              if (cartDrawerWrapper) {
                trapFocus(
                  cartDrawerWrapper,
                  lineItem.querySelector('[name="' + name + '"]'),
                );
              } else {
                lineItem.querySelector('[name="' + name + '"]').focus();
              }
            } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
              trapFocus(
                cartDrawerWrapper.querySelector(".drawer__inner-empty"),
                cartDrawerWrapper.querySelector("a"),
              );
            } else if (
              document.querySelector(".cart-item") &&
              cartDrawerWrapper
            ) {
              trapFocus(
                cartDrawerWrapper,
                document.querySelector(".cart-item__name"),
              );
            }

            /* Keep checkout gated until the gift reconciler has settled
               against this change AND proven the final cart clean. Reconcile
               is invoked DIRECTLY here (one trigger per cause - no
               calmeden:cart-update fan-out). When the theme advertises gifts
               but the reconciler is missing, or reconciliation/refresh fails,
               the transaction REJECTS (surfaced below) - a failure never
               turns into a successful transaction. */
            var bundle = window.CalmEdenBundle;
            var settle;
            if (bundle && typeof bundle.reconcile === "function") {
              settle = bundle.reconcile();
            } else if (giftSystemExpected()) {
              throw new Error("gift reconciliation unavailable");
            } else {
              settle = Promise.resolve(false);
            }
            return Promise.resolve(settle).then(function () {
              /* A quantity mutation can originate in either the drawer or
                 full cart while BOTH native forms are present. Always render
                 every present cart surface from one server snapshot—even when
                 gift reconciliation is a no-op. Merely applying cart/count
                 truth leaves the opposite form's successful `updates[]`
                 controls stale, and a later native checkout POST can then
                 overwrite the quantity this transaction just validated. */
              return refreshDrawerNow();
            });
          })
          .catch((error) => {
            this.querySelectorAll(".loading__spinner").forEach(
              function (overlay) {
                overlay.classList.add("hidden");
              },
            );
            /* Unknown state after a failed mutation: checkout stays gated,
               the GENERIC recovery text is shown (raw network/parser messages
               are not shopper copy), and the transaction rejects instead of
               resolving falsely. */
            showCartError(window.cartStrings.error);
            throw error;
          });
      }).finally(() => {
        this.disableLoading(line);
      });
    }

    updateLiveRegions(line, message) {
      var lineItemError =
        document.getElementById("Line-item-error-" + line) ||
        document.getElementById("CartDrawer-LineItemError-" + line);
      if (
        lineItemError &&
        lineItemError.querySelector(".cart-item__error-text")
      ) {
        lineItemError.querySelector(".cart-item__error-text").textContent =
          String(message || "");
      }
      if (this.lineItemStatusElement)
        this.lineItemStatusElement.setAttribute("aria-hidden", true);

      var cartStatus =
        document.getElementById("cart-live-region-text") ||
        document.getElementById("CartDrawer-LiveRegionText");
      if (!cartStatus) return;
      cartStatus.setAttribute("aria-hidden", false);
      setTimeout(function () {
        cartStatus.setAttribute("aria-hidden", true);
      }, 1000);
    }

    enableLoading(line) {
      setDesiredCheckoutDisabled(true);
      var main =
        document.getElementById("main-cart-items") ||
        document.getElementById("CartDrawer-CartItems");
      if (main) main.classList.add("cart__items--disabled");
      this.querySelectorAll(
        "#CartItem-" +
          line +
          " .loading__spinner, #CartDrawer-Item-" +
          line +
          " .loading__spinner",
      ).forEach(function (overlay) {
        overlay.classList.remove("hidden");
      });
      if (document.activeElement) document.activeElement.blur();
      if (this.lineItemStatusElement)
        this.lineItemStatusElement.setAttribute("aria-hidden", false);
    }

    disableLoading(line) {
      var main =
        document.getElementById("main-cart-items") ||
        document.getElementById("CartDrawer-CartItems");
      if (main) main.classList.remove("cart__items--disabled");
      this.querySelectorAll(
        "#CartItem-" +
          line +
          " .loading__spinner, #CartDrawer-Item-" +
          line +
          " .loading__spinner",
      ).forEach(function (overlay) {
        overlay.classList.add("hidden");
      });
    }
  }

  if (!customElements.get("cart-items"))
    customElements.define("cart-items", CartItems);

  function getSectionInnerHTML(html, selector) {
    var node = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector || ".shopify-section");
    return node ? node.innerHTML : "";
  }

  /* ------------------------------------------------------------ cart-drawer */

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.addEventListener(
        "keyup",
        (event) => event.code === "Escape" && this.close(),
      );
      var overlay = this.querySelector("#CartDrawer-Overlay");
      if (overlay) overlay.addEventListener("click", this.close.bind(this));
      this.setHeaderCartIconAccessibility();
    }

    setHeaderCartIconAccessibility() {
      // Source binds a single `#cart-icon-bubble`; CalmEden's header is owned by
      // another lane, so delegate from every cart link on the page instead.
      document.addEventListener("click", (event) => {
        var link = event.target.closest('a[href$="/cart"], a[href$="/cart/"]');
        if (!link || link.closest("cart-drawer")) return;
        event.preventDefault();
        this.open(link);
      });
      document
        .querySelectorAll('a[href$="/cart"], a[href$="/cart/"]')
        .forEach(function (link) {
          link.setAttribute("role", "button");
          link.setAttribute("aria-haspopup", "dialog");
        });
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      warmCheckout();
      setTimeout(() => {
        this.classList.add("animate", "active");
      });
      this.addEventListener(
        "transitionend",
        () => {
          var containerToTrapFocusOn = this.classList.contains("is-empty")
            ? this.querySelector(".drawer__inner-empty")
            : document.getElementById("CartDrawer");
          var focusElement =
            this.querySelector(".drawer__inner") ||
            this.querySelector(".drawer__close");
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true },
      );
      document.body.classList.add("overflow-hidden");
    }

    close() {
      this.classList.remove("active");
      removeTrapFocus(this.activeElement);
      document.body.classList.remove("overflow-hidden");
    }

    renderContents(parsedState) {
      // Anything that calls renderContents has just added a line, so the cart is
      // non-empty by definition. Clear the stale host class before painting.
      syncEmptyState(false);
      var inner = this.querySelector(".drawer__inner");
      if (inner && inner.classList.contains("is-empty"))
        inner.classList.remove("is-empty");
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach(function (section) {
        var sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        var html = parsedState.sections && parsedState.sections[section.id];
        if (!sectionElement || !html) return;
        sectionElement.innerHTML = getSectionInnerHTML(html, section.selector);
      });
      setTimeout(() => {
        var overlay = this.querySelector("#CartDrawer-Overlay");
        if (overlay) overlay.addEventListener("click", this.close.bind(this));
        this.open();
      });
    }

    getSectionsToRender() {
      return [{ id: "cart-drawer", selector: "#CartDrawer" }];
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  if (!customElements.get("cart-drawer"))
    customElements.define("cart-drawer", CartDrawer);

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector: ".drawer__inner",
        },
      ];
    }
  }

  if (!customElements.get("cart-drawer-items"))
    customElements.define("cart-drawer-items", CartDrawerItems);

  /* ---------------------------------------------------- shipping insurance */

  /*
   * Custom-coded, app-free shipping insurance. The insurance product is an
   * ordinary Shopify product (handle `shipping-insurance`), so it is added with
   * /cart/add.js, changed with /cart/change.js by the standard line-item
   * stepper, and included in cart totals natively. Checkout is never blocked
   * when it is absent.
   */
  class ShippingInsurance extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      var button = this.querySelector("[data-shipping-insurance-add]");
      if (button) button.addEventListener("click", this.add.bind(this));
    }

    get variantId() {
      return this.dataset.variantId;
    }

    add(event) {
      event.preventDefault();
      if (!this.variantId) return;
      var button = event.currentTarget;
      /* native double-click guard: two immediate clicks send ONE add */
      if (button.disabled) return;
      button.disabled = true;
      button.classList.add("loading");

      var finish = function () {
        button.disabled = false;
        button.classList.remove("loading");
      };

      var variantId = Number(this.variantId);
      var bundle = window.CalmEdenBundle;
      var reconcileGiftSet = function () {
        if (bundle && typeof bundle.reconcile === "function") {
          return bundle.reconcile();
        }
        /* The theme advertises gifts but the reconciler is gone: fail
           closed instead of enabling checkout over an unproven cart. */
        if (giftSystemExpected()) {
          return Promise.reject(new Error("gift reconciliation unavailable"));
        }
        return Promise.resolve(false);
      };

      if (!document.querySelector("cart-drawer")) {
        /* No drawer on this page: still the shared contract - desired-gate
           BEFORE enqueue, then the barrier, a validated add, gift reconcile +
           postcondition, a validated final cart read, and only THEN the
           truthful full cart page. Any failure stays desired-gated. */
        setDesiredCheckoutDisabled(true);
        transact(() => {
          return jsonValidatedAdd([{ id: variantId, quantity: 1 }])
            .then(reconcileGiftSet)
            .then(function () {
              return readCart();
            })
            .then(function () {
              window.location.href = routes.cart_url;
            });
        }).then(finish, function (error) {
          setDesiredCheckoutDisabled(true);
          showCartError((error && error.message) || window.cartStrings.error);
          finish();
        });
        return;
      }

      /* The shared serialized add path: transaction barrier, response/schema
         validation, structural drawer validation, gift reconcile with its
         zero-op postcondition, authoritative refresh of every present cart
         surface - and the final checkout gate cannot hand off while this is
         pending. */
      addItems(
        {
          items: [{ id: variantId, quantity: 1 }],
          sections: ["cart-drawer"],
          sections_url: window.location.pathname,
        },
        [{ title: "Shipping insurance", image: "", quantity: 1 }],
        null,
        {
          settle: function () {
            return reconcileGiftSet().then(function (changed) {
              if (changed) {
                /* inside the owning add transaction: the Now variant, never
                   the public queued refresh */
                return refreshDrawerNow();
              }
            });
          },
        },
      ).then(finish, finish);
    }
  }

  if (!customElements.get("shipping-insurance"))
    customElements.define("shipping-insurance", ShippingInsurance);

  /* ------------------------------------------------------- add-to-cart hook */

  /*
   * Performance timing hooks for the add-to-cart path (P8 measures identical
   * workloads off these). Marks:
   *   calmeden:tap                  submit intercepted
   *   calmeden:drawer-open          optimistic drawer painted + opened
   *   calmeden:add-response         /cart/add.js response parsed
   *   calmeden:drawer-authoritative authoritative section HTML painted
   * and matching tap-to-* measures. Guarded so environments without the
   * Performance API simply skip them.
   */
  function perfMark(name) {
    try {
      if (window.performance && performance.mark) performance.mark(name);
    } catch (e) {}
  }

  function perfMeasure(name, from, to) {
    try {
      if (window.performance && performance.measure)
        performance.measure(name, from, to);
    } catch (e) {}
  }

  /* Run fn only after the browser has actually painted, so the marks above
     measure visible UI rather than DOM mutation. Double rAF: the first
     callback still runs before that frame's paint. */
  function afterPaint(fn) {
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(fn);
      });
    } else {
      setTimeout(fn, 0);
    }
  }

  /*
   * Shared add-to-cart pipeline. There is exactly ONE owner per product form:
   *   - forms containing [data-cb-root] are owned by calmeden-bundle.js, which
   *     submits the paid units + earned $0 gifts + add-on as one `items`
   *     request through addItems() below;
   *   - every other /cart/add form is owned by the submit listener at the
   *     bottom of this file, which posts the form as-is through the same path.
   *
   * The drawer opens and optimistic rows paint synchronously, in the same task
   * as the submit, so the tap has a visible response before the network is
   * touched at all. The /cart/add.js response then replaces the whole drawer
   * with the authoritative section render; a failure replaces it with the true
   * cart state plus the real error message. No full-page reload on either path.
   *
   * `body` is either FormData (native form path) or a plain object with an
   * `items` array (bundle path). `options.settle` is an optional hook (bundle
   * gift reconciliation) that must resolve before checkout is re-enabled.
   * Resolves true when the add succeeded.
   */
  var addInFlight = false;

  function isAdding() {
    return addInFlight;
  }

  /* The add response must represent what was REQUESTED, not merely SOME
     nonempty cart. JSON multi-item path: every requested variant id present
     with at least the requested quantity. Native FormData path: the echoed
     line must be the requested variant with at least the requested
     quantity. */
  function verifyAddResponse(requestedBody, parsed) {
    if (!parsed || typeof parsed !== "object")
      throw new Error(window.cartStrings.error);
    var requested = [];
    if (requestedBody && Array.isArray(requestedBody.items)) {
      requested = requestedBody.items;
    } else if (
      typeof FormData !== "undefined" &&
      requestedBody instanceof FormData
    ) {
      var fid = Number(requestedBody.get("id"));
      var fqty = Number(requestedBody.get("quantity")) || 1;
      if (fid) requested = [{ id: fid, quantity: fqty }];
    }
    if (!requested.length) throw new Error(window.cartStrings.error);
    /* Shopify echoes {items:[...]} for JSON multi-item adds and a single
       line object for native adds - normalize. */
    var got = Array.isArray(parsed.items)
      ? parsed.items
      : parsed.id
        ? [parsed]
        : null;
    if (!got) throw new Error(window.cartStrings.error);
    requested.forEach(function (req) {
      var found = null;
      got.forEach(function (item) {
        var vid = Number(item.variant_id || item.id);
        if (vid === Number(req.id)) found = item;
      });
      if (!found || Number(found.quantity) < Number(req.quantity)) {
        throw new Error("add response mismatch");
      }
    });
  }

  /* A change (quantity/removal) response is staged and validated BEFORE the
     first live DOM write: numeric item_count, an items array, a sections
     object, every requested section, and every exact selector present in the
     staged HTML. Any gap means zero DOM mutation and a gated control set. */
  function validateChangeState(parsedState, sectionsToRender) {
    if (
      !parsedState ||
      typeof parsedState.item_count !== "number" ||
      !Array.isArray(parsedState.items)
    ) {
      throw new Error("invalid change payload");
    }
    if (!parsedState.sections || typeof parsedState.sections !== "object") {
      throw new Error("missing sections");
    }
    sectionsToRender.forEach(function (section) {
      var html = parsedState.sections[section.section];
      if (typeof html !== "string" || !html.length) {
        throw new Error("missing section: " + section.section);
      }
      var doc = new DOMParser().parseFromString(html, "text/html");
      if (!doc.querySelector(section.selector)) {
        throw new Error("missing selector: " + section.selector);
      }
    });
  }

  /* A successful JSON add is only authoritative when it carries a usable
     server-rendered cart-drawer section; without one the optimistic markup
     must never become the truth and checkout must stay gated. The check is
     STRUCTURAL (DOMParser, compare sections/cart-drawer.liquid) and scoped to
     the drawer root: comments, scripts, bare text, a maintenance div that
     merely mentions CartDrawer, span or text-input masquerades, a detached
     sibling form or checkout button, a missing form, missing items, or wrong
     form ownership are all rejected. */
  function isValidDrawerSection(sections) {
    var html = sections && sections["cart-drawer"];
    if (typeof html !== "string") return false;
    var trimmed = html.replace(/^\s+|\s+$/g, "");
    if (!trimmed.length) return false;
    if (trimmed.indexOf("<!--") === 0) return false; /* comment, not markup */
    if (/<script[\s>]/i.test(trimmed)) return false; /* script, not markup */
    if (trimmed.charAt(0) !== "<") return false; /* bare text */
    if (typeof DOMParser === "undefined")
      return false; /* no parser, no trust */
    var doc;
    try {
      doc = new DOMParser().parseFromString(trimmed, "text/html");
    } catch (e) {
      return false;
    }
    if (!doc) return false;
    /* Scoped to the drawer root: form INSIDE div#CartDrawer, items INSIDE the
       form, checkout INSIDE the root with submit/checkout/form= ownership. */
    var root = doc.querySelector("div#CartDrawer");
    if (!root) return false; /* wrong root element or missing */
    var form = root.querySelector("form#CartDrawer-Form");
    if (!form) return false; /* missing, or a detached sibling of the root */
    var items = form.querySelector("div#CartDrawer-CartItems");
    if (!items) return false; /* missing, wrong tag, or outside the form */
    var checkout = root.querySelector("button#CartDrawer-Checkout");
    if (!checkout) return false;
    if (checkout.getAttribute("type") !== "submit") return false;
    if (checkout.getAttribute("name") !== "checkout") return false;
    if (checkout.getAttribute("form") !== "CartDrawer-Form") return false;
    return true;
  }

  function addItems(body, optimisticLines, form, options) {
    var drawer = document.querySelector("cart-drawer");
    if (!drawer || addInFlight) return Promise.resolve(false);
    addInFlight = true;
    var opts = options || {};

    /* Everything in this block runs in the SAME task as the submit: the mutex
       is held, checkout is gated, the tap is marked, guaranteed feedback
       paints and the drawer opens - before any asynchronous payload
       preparation (the bundle's existing-cart pre-read) begins, so the
       tap-to-* measures below span that preparation. */
    perfMark("calmeden:tap");
    /* Unproven from the tap; the barrier's own gate lands when transact() is
       invoked below, still in this same task. */
    setDesiredCheckoutDisabled(true);

    // --- instant UI, before any fetch ---------------------------------------
    try {
      paintOptimisticLines(drawer, optimisticLines || []);
      drawer.open();
    } catch (e) {
      /* placeholder and even the open animation are niceties; never let them
         block the add itself (the mutex must survive a sync UI failure) */
    }
    afterPaint(function () {
      perfMark("calmeden:drawer-open");
      perfMeasure(
        "calmeden:tap-to-drawer-open",
        "calmeden:tap",
        "calmeden:drawer-open",
      );
    });

    return transact(function () {
      return Promise.resolve()
        .then(function () {
          return typeof opts.prepare === "function" ? opts.prepare() : body;
        })
        .then(function (preparedBody) {
          var isFormData =
            typeof FormData !== "undefined" && preparedBody instanceof FormData;
          var payload = isFormData
            ? preparedBody
            : JSON.stringify(preparedBody);
          return fetch(routes.cart_add_url + ".js", {
            method: "POST",
            headers: isFormData
              ? { Accept: "application/javascript" }
              : {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                },
            body: payload,
          }).then(function (response) {
            return response.json().then(function (parsed) {
              if (!response.ok) {
                throw new Error(
                  (parsed && (parsed.description || parsed.message)) ||
                    "add failed: HTTP " + response.status,
                );
              }
              return { parsed: parsed, requested: preparedBody };
            });
          });
        })
        .then(function (result) {
          var parsed = result.parsed;
          perfMark("calmeden:add-response");
          perfMeasure(
            "calmeden:tap-to-add-response",
            "calmeden:tap",
            "calmeden:add-response",
          );
          if (parsed.status)
            throw new Error(parsed.description || parsed.message);
          verifyAddResponse(result.requested, parsed);
          if (!isValidDrawerSection(parsed.sections))
            throw new Error(window.cartStrings.error);
          // Replaces #CartDrawer wholesale, so the optimistic rows go with it.
          drawer.renderContents({ id: parsed.id, sections: parsed.sections });
          /* renderContents swapped in the server-rendered button; the gate is
             re-applied to the NEW node synchronously, before any await. */
          regateIfBusy();
          afterPaint(function () {
            perfMark("calmeden:drawer-authoritative");
            perfMeasure(
              "calmeden:tap-to-authoritative-drawer",
              "calmeden:tap",
              "calmeden:drawer-authoritative",
            );
          });
          /* Checkout stays gated until the settle hook (gift reconciliation
             with its zero-op postcondition on the bundle path) has finished. */
          regateIfBusy();
          var settle = typeof opts.settle === "function" ? opts.settle() : null;
          return Promise.resolve(settle).then(
            function () {
              return readCart().then(function (cart) {
                updateHeaderCartCount(cart.item_count);
                syncEmptyState(cart.item_count === 0);
                /* Truthful even in the pathological case: only a non-empty
                   cart gets an enabled checkout - and even then, only once
                   the barrier has fully drained. */
                setDesiredCheckoutDisabled(cart.item_count === 0);
                return true;
              });
            },
            function (settleError) {
              /* Reconciliation or its postcondition FAILED: the cart cannot
                 be proven truthful, so checkout stays gated, the shopper sees
                 the error, and the failure REJECTS - it is never converted
                 into a soft false and never flows into the unsafe generic
                 recovery below (the drawer is already showing the
                 authoritative add render; re-rendering it would hide the gate
                 violation). */
              showCartError(
                (settleError && settleError.message) ||
                  window.cartStrings.error,
              );
              var marked =
                settleError instanceof Error
                  ? settleError
                  : new Error(String(settleError));
              marked.calmedenSettleFailed = true;
              throw marked;
            },
          );
        })
        .catch(function (error) {
          /* Recovery runs INSIDE the barrier. A settle failure already
             surfaced its own gate-keeping error and never comes here. */
          if (error && error.calmedenSettleFailed) throw error;
          document
            .querySelectorAll(".cart-item--pending")
            .forEach(function (row) {
              row.remove();
            });
          return restoreTrueCartStateNow(drawer, error && error.message).then(
            function () {
              return false;
            },
            function () {
              /* Recovery itself failed: the cart state is UNKNOWN - checkout
                 stays gated and the shopper gets recovery text. */
              showCartError(window.cartStrings.error);
              return false;
            },
          );
        });
    }).then(
      function (result) {
        addInFlight = false;
        return result;
      },
      function (error) {
        addInFlight = false;
        throw error;
      },
    );
  }

  /* Re-read the server-rendered cart surfaces (used by the gift reconciler
     after it repairs lines outside the add flow). Refreshes the drawer plus,
     on the /cart page, the main cart items and footer sections when they
     exist on this page. Fail closed: the response status, every required
     section and the cart body are validated, checkout is re-gated after the
     DOM replacement until the validated cart read lands, and ANY failure
     rejects - never a silent false. */
  function refreshDrawer() {
    /* Queued public refresh: desired-gate BEFORE enqueueing, so a failed
       refresh stays gated even when the prior desired state was false; the
       Now variant (inside an owning transaction) records the authoritative
       final state on success. */
    setDesiredCheckoutDisabled(true);
    return transact(refreshDrawerNow);
  }

  function refreshDrawerNow() {
    var names = ["cart-drawer"];
    var mainTargets = [];
    [
      ["main-cart-items", "#main-cart-items .js-contents"],
      ["main-cart-footer", "#main-cart-footer .js-contents"],
    ].forEach(function (pair) {
      var el = document.getElementById(pair[0]);
      if (el && el.dataset.id) {
        if (names.indexOf(el.dataset.id) === -1) names.push(el.dataset.id);
        mainTargets.push({
          section: el.dataset.id,
          container: el,
          selector: pair[1],
        });
      }
    });
    if (!document.querySelector("cart-drawer") && !mainTargets.length) {
      return Promise.resolve(false);
    }
    return fetch(
      window.location.pathname +
        "?sections=" +
        encodeURIComponent(names.join(",")) +
        "&cb=" +
        Date.now(),
    )
      .then(function (response) {
        if (!response || !response.ok) throw new Error("refresh read failed");
        return response.json();
      })
      .then(function (sections) {
        /* STAGE and validate everything - the drawer structure, every
           present main-cart fragment and every exact selector, plus the cart
           body - before the first live DOM write. Any gap means zero
           mutation and a still-gated control set. */
        if (!sections || typeof sections !== "object") {
          throw new Error("invalid sections payload");
        }
        var target = document.querySelector("#CartDrawer");
        if (document.querySelector("cart-drawer")) {
          if (!target) throw new Error("drawer host missing");
          if (!isValidDrawerSection(sections))
            throw new Error("invalid cart-drawer section");
        }
        mainTargets.forEach(function (t) {
          var sectionHtml = sections[t.section];
          if (typeof sectionHtml !== "string" || !sectionHtml.length) {
            throw new Error("missing section: " + t.section);
          }
          var doc = new DOMParser().parseFromString(sectionHtml, "text/html");
          if (!doc.querySelector(t.selector)) {
            throw new Error("missing selector: " + t.selector);
          }
        });
        return readCart().then(function (cart) {
          return { sections: sections, cart: cart, target: target };
        });
      })
      .then(function (stage) {
        /* Everything validated: render atomically, then re-apply the gate to
           the fresh nodes before any await. */
        if (stage.target) {
          stage.target.innerHTML = getSectionInnerHTML(
            stage.sections["cart-drawer"],
            "#CartDrawer",
          );
        }
        mainTargets.forEach(function (t) {
          var elToReplace =
            t.container.querySelector(t.selector) || t.container;
          elToReplace.innerHTML = getSectionInnerHTML(
            stage.sections[t.section],
            t.selector,
          );
        });
        regateIfBusy();
        applyAuthoritativeCart(stage.cart);
        return true;
      });
  }

  function showCartError(message) {
    var errors =
      document.getElementById("cart-errors") ||
      document.getElementById("CartDrawer-CartErrors");
    if (errors) errors.textContent = message;
  }

  /* Narrow gate API for the gift reconciler (calmeden-bundle.js): gate,
     inspect, and report - nothing else about checkout is exposed. */
  function getCheckoutGated() {
    var button = document.getElementById("CartDrawer-Checkout");
    return button ? !!button.disabled : null;
  }

  window.CalmEdenCart = {
    addItems: addItems,
    /* Queued public refresh; call the Now variant only from inside an owning
       transaction (bundle settle hooks, the checkout gate). */
    refreshDrawer: refreshDrawer,
    refreshDrawerNow: refreshDrawerNow,
    transact: transact,
    readOptimisticLine: readOptimisticLine,
    isAdding: isAdding,
    isValidDrawerSection: isValidDrawerSection,
    setCheckoutGated: setDesiredCheckoutDisabled,
    getCheckoutGated: getCheckoutGated,
    applyCartTruth: applyAuthoritativeCart,
    getLastAuthoritativeCart: getLastAuthoritativeCart,
    showCartError: showCartError,
  };

  document.addEventListener("submit", function (event) {
    if (event.defaultPrevented) return;
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || form.action.indexOf("/cart/add") === -1) return;
    /* Bundle forms are owned by calmeden-bundle.js only after that asset has
       positively declared this exact form ready. If the script is missing,
       blocked, or its config failed, a native submit would add only the paid
       line and silently omit the advertised gifts/add-on. Fail closed. */
    if (form.querySelector("[data-cb-root]")) {
      var bundleOwner = window.CalmEdenBundle;
      if (
        !bundleOwner ||
        typeof bundleOwner.ownsForm !== "function" ||
        bundleOwner.ownsForm(form) !== true
      ) {
        event.preventDefault();
        setDesiredCheckoutDisabled(true);
        showCartError(window.cartStrings.error);
      }
      return;
    }
    var drawer = document.querySelector("cart-drawer");
    if (!drawer) return;

    event.preventDefault();
    // In-flight mutex: a double submit while an add is running is swallowed.
    if (addInFlight) return;

    var formData = new FormData(form);
    formData.append("sections", "cart-drawer");
    formData.append("sections_url", window.location.pathname);

    var genericOptions = {};
    if (giftSystemExpected()) {
      /* Collection/search quick-add forms do not render the PDP bundle root,
         but an eligible product added there must still converge to the same
         earned gift set. Keep this inside the owning add transaction and
         refresh only when reconciliation actually changed gift lines. */
      genericOptions.settle = function () {
        var bundle = window.CalmEdenBundle;
        if (!bundle || typeof bundle.reconcile !== "function") {
          return Promise.reject(new Error("gift reconciliation unavailable"));
        }
        return bundle.reconcile().then(function (changed) {
          return changed ? refreshDrawerNow() : null;
        });
      };
    }

    addItems(
      formData,
      [readOptimisticLine(form, formData)],
      form,
      genericOptions,
    );
  });

  // Sync the badge/empty state once on load: a page rendered while the cart
  // was empty and then filled in another tab would otherwise keep a stale
  // `is-empty` host class. Cosmetic only - failures change nothing.
  document.addEventListener("DOMContentLoaded", function () {
    if (!document.querySelector("cart-drawer")) return;
    if (giftSystemExpected()) {
      /* calmeden-bundle.js owns the authoritative boot read. A missing bundle
         asset is unknown cart/gift state, so gate visibly instead of issuing
         a redundant cosmetic read or trusting server markup. */
      if (
        !window.CalmEdenBundle ||
        typeof window.CalmEdenBundle.reconcile !== "function"
      ) {
        setDesiredCheckoutDisabled(true);
        showCartError(window.cartStrings.error);
      }
      return;
    }
    readCart()
      .then(function (cart) {
        applyAuthoritativeCart(cart);
      })
      .catch(function () {});
  });

  /* ---------------------------------------------------- final checkout gate */

  /*
   * Before EITHER native checkout form (the drawer's CartDrawer-Form or the
   * /cart page `cart` form) may hand off to /checkout, the gift reconciler
   * must settle with its zero-op postcondition - and the submit waits on the
   * shared mutation barrier like every other path. On success the CURRENT
   * form and its CURRENT owned [name=checkout] submitter are re-acquired
   * (refresh may have replaced both - never the detached event.submitter)
   * and the native submit is approved exactly once. The one-shot flag is
   * cleared on every success and failure; a handoff exception re-gates and
   * surfaces the error. If the theme advertises gifts (the data-cb-global
   * island) but the reconciler is missing, this fails closed instead of
   * allowing a native checkout over an unproven cart.
   */
  var checkoutIntentPending = false;

  function checkoutControls(formId) {
    var target = document.getElementById(formId);
    var buttonId =
      formId === "CartDrawer-Form" ? "CartDrawer-Checkout" : "checkout";
    var submitter = document.getElementById(buttonId);
    if (
      !(target instanceof HTMLFormElement) ||
      !submitter ||
      submitter.form !== target ||
      String(submitter.getAttribute("name") || "") !== "checkout" ||
      String(submitter.getAttribute("type") || "submit").toLowerCase() !==
        "submit"
    ) {
      throw new Error("checkout form unavailable");
    }
    return { form: target, submitter: submitter };
  }

  function clearCheckoutApproval(formId) {
    var current = document.getElementById(formId);
    if (!current) return;
    try {
      delete current.__calmedenCheckoutApproved;
    } catch (_error) {
      current.__calmedenCheckoutApproved = null;
    }
  }

  function finalCartAfterReconcile(bundle, changed) {
    var finalCart = changed
      ? getLastAuthoritativeCart()
      : bundle && typeof bundle.getLastSettledCart === "function"
        ? bundle.getLastSettledCart()
        : null;
    var finalCartPromise = isValidCartPayload(finalCart)
      ? Promise.resolve(finalCart)
      : readCart();
    return finalCartPromise.then(function (cart) {
      /* A changed reconcile may be followed by refreshDrawerNow(), whose
         newer /cart.js response supersedes the snapshot that passed the gift
         postcondition. The exact cart authorising native checkout must itself
         be zero-op under the same planner. Never inherit approval merely from
         an older settled cart. */
      if (
        !bundle ||
        typeof bundle.planReconcileOps !== "function" ||
        bundle.planReconcileOps(cart.items).length
      ) {
        throw new Error("cart gift state is not settled");
      }
      return applyAuthoritativeCart(cart);
    });
  }

  function assertCheckoutCart(bundle, cart) {
    if (
      !isValidCartPayload(cart) ||
      cart.item_count <= 0 ||
      !bundle ||
      typeof bundle.planReconcileOps !== "function" ||
      bundle.planReconcileOps(cart.items).length
    ) {
      throw new Error("checkout cart is not settled");
    }
    return cart;
  }

  function nativeCheckoutHandoff(formId, bundle, approvedCart) {
    if (transactionBusy() || desiredCheckoutDisabled !== false) {
      throw new Error("checkout barrier not idle");
    }
    /* Revalidate synchronously in the same call stack as requestSubmit. A
       later queued transaction cannot inherit an earlier cart approval, and
       no await separates this final planner check from the side effect. */
    assertCheckoutCart(bundle, approvedCart);
    if (getLastAuthoritativeCart() !== approvedCart) {
      throw new Error("checkout cart was superseded");
    }
    var controls = checkoutControls(formId);
    var target = controls.form;
    var submitter = controls.submitter;
    if (submitter.disabled || typeof target.requestSubmit !== "function") {
      throw new Error("checkout submitter unavailable");
    }

    /* The token is the exact owned submitter, not a boolean. The synchronous
       submit event must consume it; otherwise a later click could bypass the
       gate, so clear it and fail closed. */
    target.__calmedenCheckoutApproved = submitter;
    try {
      target.requestSubmit(submitter);
    } catch (handoffError) {
      clearCheckoutApproval(formId);
      throw handoffError;
    }
    if (target.__calmedenCheckoutApproved === submitter) {
      clearCheckoutApproval(formId);
      throw new Error("checkout handoff was not consumed");
    }
    return true;
  }

  function prepareCheckoutHandoff(formId, bundle, attempt) {
    var pass = Number(attempt) || 1;
    setDesiredCheckoutDisabled(true);
    return transact(function () {
      var changed = false;
      return Promise.resolve(bundle.reconcile())
        .then(function (didChange) {
          changed = !!didChange;
          /* private composition inside this owning transaction */
          return changed ? refreshDrawerNow() : null;
        })
        .then(function () {
          return finalCartAfterReconcile(bundle, changed);
        })
        .then(function (finalCart) {
          assertCheckoutCart(bundle, finalCart);
          /* Keep the desired gate closed when this transaction drains. The
             button is released only synchronously beside native handoff. */
          setDesiredCheckoutDisabled(true);
          return finalCart;
        });
    }).then(function (approvedCart) {
      if (transactionBusy()) {
        if (pass >= 3) throw new Error("checkout cart kept changing");
        return waitForTransactionsIdle().then(function () {
          return prepareCheckoutHandoff(formId, bundle, pass + 1);
        });
      }
      /* No task can interleave between this release, the repeated planner
         check in nativeCheckoutHandoff, and requestSubmit. */
      setDesiredCheckoutDisabled(false);
      return nativeCheckoutHandoff(formId, bundle, approvedCart);
    });
  }

  document.addEventListener("submit", function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.id !== "CartDrawer-Form" && form.id !== "cart") return;

    var approval = form.__calmedenCheckoutApproved;
    if (approval) {
      clearCheckoutApproval(form.id);
      if (
        event.submitter !== approval ||
        approval.form !== form ||
        approval.disabled
      ) {
        event.preventDefault();
        setDesiredCheckoutDisabled(true);
        showCartError(window.cartStrings.error);
      }
      return;
    }

    if (checkoutIntentPending) {
      event.preventDefault();
      return;
    }

    var bundle = window.CalmEdenBundle;
    if (!bundle || typeof bundle.reconcile !== "function") {
      if (giftSystemExpected()) {
        event.preventDefault();
        setDesiredCheckoutDisabled(true);
        showCartError(window.cartStrings.error);
      }
      return; /* no gift system advertised: native */
    }

    event.preventDefault();
    checkoutIntentPending = true;
    var formId = form.id;
    /* Gate BEFORE enqueuing: the submit waits on the shared barrier like
       every other mutation path. */
    setDesiredCheckoutDisabled(true);
    prepareCheckoutHandoff(formId, bundle, 1)
      .then(
        function () {
          checkoutIntentPending = false;
        },
        function () {
          /* ONE outer cleanup for every failure: no surviving approval token,
             checkout re-gated, and only generic recovery copy is exposed. */
          checkoutIntentPending = false;
          clearCheckoutApproval(formId);
          setDesiredCheckoutDisabled(true);
          showCartError(window.cartStrings.error);
        },
      );
  });
})();
